import { DndContext, DragOverlay, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import type { CollisionDetection, DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CloudDone, CloudOutlined, Link as LinkIcon, Visibility } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import {
  extractSectionFromChapterSectionHeading,
  spliceChapterSectionIntoSource,
} from '@shared/ast/extractChapterSection'
import type { ChapterSectionDependency } from '@shared/chapterSectionDependencies'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import SectionDependencyChip from './dependencies/SectionDependencyChip'
import DroppableContainer from './organize-chapters/DroppableContainer'
import SearchPreviewPanel from '../../../components/search/SearchPreviewPanel'
import { Book, ContentFile, apiService } from '../../../services/api'
import { ContentAST } from '../../../transforms/ast'
import { logger } from '../../../utils/logger'
import type { ChapterSectionEntry } from '../hooks/useChapterSectionsBook'
import type { SectionDependencyView } from '../hooks/useSectionDependencies'

interface SortableFlatRowProps {
  sectionKey: string
  entry: ChapterSectionEntry
  bookFontFamily?: string
  bookDirection: 'ltr' | 'rtl'
  isDragOverlay?: boolean
  /** When true, render the publish cloud icon. Placed sections column only. */
  showPublishControl?: boolean
  /** Click handler for the publish cloud. Receives the entry's chapter + headingBlockId. */
  onPublishClick?: (chapter: ContentFile, headingBlockId: string, ev: React.MouseEvent) => void
  /** When true, render a spinner instead of the cloud icon (publish in-flight). */
  publishBusy?: boolean
  /** When true, disable the publish control entirely. */
  readOnly?: boolean
  /** When provided, render an eye icon that opens the section preview/editor. */
  onPreview?: (sectionKey: string) => void
  /** Shared dependency view; the chip renders only when this section has deps. */
  dependencies?: SectionDependencyView
  onEditDependency?: (dep: ChapterSectionDependency) => void
  onRemoveDependency?: (depId: string) => void
  /** Link mode: dragging is off and a click picks the row as an endpoint. */
  linkMode?: boolean
  /** Link mode: this row is the picked `from` endpoint, waiting for a target. */
  isPickedFrom?: boolean
  onLinkClick?: (sectionKey: string) => void
}

/**
 * Compact grid-friendly row used inside OrganizeFlatSectionsDialog.
 * Constrained height + ellipsized text so many sections fit on screen at
 * once. Distinct from `SortableSectionRow`, which is taller and built for the
 * chapters organizer.
 */
function SortableFlatRow({
  sectionKey,
  entry,
  bookFontFamily,
  bookDirection,
  isDragOverlay,
  showPublishControl,
  onPublishClick,
  publishBusy,
  readOnly,
  onPreview,
  dependencies,
  onEditDependency,
  onRemoveDependency,
  linkMode,
  isPickedFrom,
  onLinkClick,
}: SortableFlatRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sectionKey,
    disabled: isDragOverlay || linkMode,
  })
  const ancestorPath = [entry.chapter.title, ...entry.ancestorLabels]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' · ')

  const sectionPublished = Boolean(entry.chapter.publishedSections?.includes(entry.headingBlockId))

  // touchAction: 'manipulation' so the dialog still scrolls on mobile — the
  // TouchSensor's delay activation already gates drag behind a long-press.
  const style: React.CSSProperties = isDragOverlay
    ? { touchAction: 'manipulation' }
    : {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : 1,
        touchAction: 'manipulation',
      }

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-section-key={sectionKey}
      onClick={linkMode ? () => onLinkClick?.(sectionKey) : undefined}
      {...attributes}
      {...(linkMode ? {} : listeners)}
    >
      <Box
        sx={{
          px: 1,
          py: 0.5,
          borderRadius: 0.75,
          bgcolor: isPickedFrom ? 'primary.50' : 'action.hover',
          border: '1px solid',
          borderColor: isPickedFrom ? 'primary.main' : 'transparent',
          cursor: linkMode ? 'pointer' : 'grab',
          minHeight: 40,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          '&:hover': { bgcolor: 'action.selected' },
          '&:hover .flat-row-preview-btn': { opacity: 0.9 },
          ...(linkMode ? {} : { '&:active': { cursor: 'grabbing' } }),
          userSelect: 'none',
        }}
      >
        {showPublishControl && (
          <IconButton
            size="small"
            disabled={publishBusy || readOnly}
            // Stop pointer/click propagation so dnd-kit's drag listeners (on the
            // wrapping div) don't arm when the author taps the cloud.
            onPointerDown={(ev) => ev.stopPropagation()}
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.stopPropagation()
              if (readOnly || publishBusy) {
                return
              }
              onPublishClick?.(entry.chapter, entry.headingBlockId, ev)
            }}
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              p: 0.25,
              color: 'var(--icon-default)',
              '&:hover': { color: 'var(--icon-hover)', bgcolor: 'var(--bg-hover)' },
              ...(readOnly && { opacity: 0.35 }),
            }}
            title={
              readOnly
                ? undefined
                : publishBusy
                  ? 'Working…'
                  : sectionPublished
                    ? 'Published — click to unpublish • Shift+click to re-publish'
                    : 'Publish this chapter section'
            }
          >
            {publishBusy ? (
              <CircularProgress size={14} thickness={5} sx={{ color: 'var(--icon-default)' }} />
            ) : sectionPublished ? (
              <CloudDone fontSize="small" />
            ) : (
              <CloudOutlined fontSize="small" />
            )}
          </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.1 }}>
          <Typography
            sx={{
              fontFamily: bookFontFamily || 'SBL Hebrew',
              fontSize: '0.85rem',
              fontWeight: 500,
              lineHeight: 1.2,
              direction: bookDirection,
              textAlign: bookDirection === 'rtl' ? 'right' : 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'text.primary',
            }}
          >
            {entry.displayText}
          </Typography>
          {ancestorPath && (
            <Typography
              sx={{
                fontSize: '0.65rem',
                lineHeight: 1.2,
                color: 'text.secondary',
                opacity: 0.65,
                direction: bookDirection,
                textAlign: bookDirection === 'rtl' ? 'right' : 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {ancestorPath}
            </Typography>
          )}
        </Box>
        {dependencies && !isDragOverlay && (
          <Box
            sx={{ flexShrink: 0 }}
            onPointerDown={(ev) => ev.stopPropagation()}
            onMouseDown={(ev) => ev.stopPropagation()}
          >
            <SectionDependencyChip
              sectionKey={sectionKey}
              view={dependencies}
              bookDirection={bookDirection}
              bookFontFamily={bookFontFamily}
              onRemove={(depId) => onRemoveDependency?.(depId)}
              onEdit={(dep) => onEditDependency?.(dep)}
            />
          </Box>
        )}
        {onPreview && !isDragOverlay && (
          <IconButton
            className="flat-row-preview-btn"
            size="small"
            onPointerDown={(ev) => ev.stopPropagation()}
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.stopPropagation()
              onPreview(sectionKey)
            }}
            title="Preview section"
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              p: 0.25,
              opacity: 0.25,
              color: 'text.secondary',
              transition: 'opacity 0.15s ease',
              '&:hover': { bgcolor: 'action.hover', opacity: 1 },
            }}
          >
            <Visibility sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </div>
  )
}

const PLACED_ID = '__flat_placed__'
const UNPLACED_ID = '__flat_unplaced__'

interface OrganizeFlatSectionsDialogProps {
  open: boolean
  onClose: () => void
  /** Section keys currently in flatSectionsOrder (preserves order). */
  currentOrder: string[]
  /** All canonical section keys in the book (chapter-walk order). Used as the pool. */
  allSectionKeys: string[]
  entryByKey: Map<string, ChapterSectionEntry>
  onSave: (next: string[]) => Promise<void>
  bookFontFamily?: string
  bookDirection?: 'ltr' | 'rtl'
  /** Toggle publish for a section. Surfaced as a cloud icon in the Placed sections column. */
  onChapterSectionPublish?: (chapter: ContentFile, headingBlockId: string, ev: React.MouseEvent) => void
  /** chapterSectionKey of a section currently mid-publish (for the spinner). */
  chapterSectionPublishBusyKey?: string | null
  readOnly?: boolean
  /** Required for the per-row preview/edit feature. Without it, the eye icon is hidden. */
  bookId?: string
  book?: Book | null
  /** Called after a successful section-content save. Host should refresh its currentChapter state. */
  onSectionContentSaved?: (filename: string, updatedFile: ContentFile) => void
  /**
   * Shared dependency view. Dependencies are the same set of section-key pairs in
   * either structure, so this dialog shows exactly what the chapters organizer shows.
   */
  dependencies?: SectionDependencyView
  onEditDependency?: (dep: ChapterSectionDependency) => void
  onRemoveDependency?: (depId: string) => void
  /** Opens the dependency editor prefilled with a picked pair. Enables link mode. */
  onCreateDependency?: (fromKey: string, toKey: string) => void
}

/**
 * Manage the flat sections sequence for `book.settings.structure === 'flat'`.
 * Author drags sections between "Unplaced" (in the book but not yet placed)
 * and "Placed" (the actual flat reader order). The order inside "Placed" is
 * preserved verbatim.
 */
export default function OrganizeFlatSectionsDialog({
  open,
  onClose,
  currentOrder,
  allSectionKeys,
  entryByKey,
  onSave,
  bookFontFamily,
  bookDirection = 'ltr',
  onChapterSectionPublish,
  chapterSectionPublishBusyKey,
  readOnly,
  bookId,
  book,
  onSectionContentSaved,
  dependencies,
  onEditDependency,
  onRemoveDependency,
  onCreateDependency,
}: OrganizeFlatSectionsDialogProps) {
  // Link mode borrows the chapters organizer's interaction: pick a section, then pick
  // its counterpart. Unlike there, the pair is persisted immediately by the editor-level
  // dialog rather than held as dialog-local draft state alongside the order.
  const [linkMode, setLinkMode] = useState(false)
  const [pickedFromKey, setPickedFromKey] = useState<string | null>(null)

  const handleLinkClick = useCallback(
    (sectionKey: string) => {
      if (!pickedFromKey) {
        setPickedFromKey(sectionKey)
        return
      }
      if (pickedFromKey === sectionKey) {
        setPickedFromKey(null)
        return
      }
      onCreateDependency?.(pickedFromKey, sectionKey)
      setPickedFromKey(null)
    },
    [pickedFromKey, onCreateDependency],
  )
  const initialOrder = useMemo(() => currentOrder.filter((k) => entryByKey.has(k)), [currentOrder, entryByKey])
  const initialUnplaced = useMemo(() => {
    const orderedSet = new Set(initialOrder)
    return allSectionKeys.filter((k) => !orderedSet.has(k))
  }, [allSectionKeys, initialOrder])

  const [draftOrder, setDraftOrder] = useState<string[]>(initialOrder)
  const [draftUnplaced, setDraftUnplaced] = useState<string[]>(initialUnplaced)
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const initialOrderRef = useRef(initialOrder)
  initialOrderRef.current = initialOrder

  // --- Section preview state (mirrors OrganizeChaptersDialog) ---
  const [previewKey, setPreviewKey] = useState<string | null>(null)
  const [previewAst, setPreviewAst] = useState<ContentAST | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewDirty, setPreviewDirty] = useState(false)
  const [previewSaving, setPreviewSaving] = useState(false)
  const [previewRecentlySaved, setPreviewRecentlySaved] = useState(false)
  const sourceCacheRef = useRef<Map<string, ContentFile>>(new Map())
  const recentlySavedTimerRef = useRef<number | null>(null)

  // Reset draft when the dialog opens with fresh inputs.
  useEffect(() => {
    if (open) {
      setDraftOrder(initialOrder)
      setDraftUnplaced(initialUnplaced)
      setActiveDragId(null)
    }
  }, [open, initialOrder, initialUnplaced])

  // Reset preview when the dialog closes so it doesn't flash stale content on reopen.
  useEffect(() => {
    if (!open) {
      setPreviewKey(null)
      setPreviewAst(null)
      setPreviewError(null)
      setPreviewLoading(false)
      sourceCacheRef.current.clear()
      setPreviewDirty(false)
      setPreviewSaving(false)
      setPreviewRecentlySaved(false)
      if (recentlySavedTimerRef.current) {
        window.clearTimeout(recentlySavedTimerRef.current)
        recentlySavedTimerRef.current = null
      }
    }
  }, [open])

  useEffect(() => {
    return () => {
      if (recentlySavedTimerRef.current) {
        window.clearTimeout(recentlySavedTimerRef.current)
      }
    }
  }, [])

  // Load the previewed section's AST slice when the selection changes.
  useEffect(() => {
    if (!open || !previewKey || !bookId) {
      return
    }
    const entry = entryByKey.get(previewKey)
    if (!entry) {
      setPreviewAst(null)
      setPreviewError(null)
      return
    }

    const filename = entry.chapter.filename
    const headingBlockId = entry.headingBlockId
    let cancelled = false

    const sliceFromSource = (source: ContentFile): void => {
      const ast = source.authorContent
      if (!ast) {
        setPreviewAst(null)
        setPreviewError('Section has no content yet')
        return
      }
      const result = extractSectionFromChapterSectionHeading(ast, headingBlockId)
      if (!result) {
        setPreviewAst(null)
        setPreviewError('Section not found in source file')
        return
      }
      setPreviewAst(result.slice)
      setPreviewError(null)
    }

    const cached = sourceCacheRef.current.get(filename)
    if (cached) {
      sliceFromSource(cached)
      setPreviewLoading(false)
      return
    }

    setPreviewLoading(true)
    setPreviewError(null)
    ;(async () => {
      try {
        const res = await apiService.getChapter(bookId, filename)
        if (cancelled) {
          return
        }
        if (!res.success || !res.data) {
          setPreviewAst(null)
          setPreviewError('Failed to load preview')
          return
        }
        sourceCacheRef.current.set(filename, res.data)
        sliceFromSource(res.data)
      } catch (err) {
        if (cancelled) {
          return
        }
        logger.warn('Section preview load failed', err)
        setPreviewAst(null)
        setPreviewError('Failed to load preview')
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, previewKey, bookId, entryByKey])

  const previewEntry = previewKey ? (entryByKey.get(previewKey) ?? null) : null
  const previewTitle = previewEntry?.displayText ?? null
  const previewOpen = Boolean(previewKey)

  const resetPreviewEditState = useCallback(() => {
    setPreviewDirty(false)
    setPreviewSaving(false)
    setPreviewRecentlySaved(false)
    if (recentlySavedTimerRef.current) {
      window.clearTimeout(recentlySavedTimerRef.current)
      recentlySavedTimerRef.current = null
    }
  }, [])

  const handlePreview = useCallback(
    (key: string) => {
      if (previewKey === key) {
        return
      }
      if (previewDirty && !window.confirm('Discard unsaved changes to this section?')) {
        return
      }
      // Clear stale AST first so LexicalEditor unmounts before the new section
      // remounts with the freshly-loaded AST. See OrganizeChaptersDialog for the
      // longer note on why this is necessary.
      setPreviewAst(null)
      setPreviewError(null)
      setPreviewLoading(true)
      setPreviewKey(key)
      resetPreviewEditState()
    },
    [previewKey, previewDirty, resetPreviewEditState],
  )

  const handlePreviewChange = useCallback((_ast: ContentAST) => {
    setPreviewDirty(true)
    setPreviewRecentlySaved(false)
  }, [])

  const handlePreviewSave = useCallback(
    async (flushedAst: ContentAST) => {
      if (!previewKey || !bookId) {
        return
      }
      const entry = entryByKey.get(previewKey)
      if (!entry) {
        return
      }
      const filename = entry.chapter.filename
      const headingBlockId = entry.headingBlockId

      setPreviewSaving(true)
      try {
        const res = await apiService.getChapter(bookId, filename)
        if (!res.success || !res.data?.authorContent) {
          toast.error('Failed to load source for save')
          return
        }
        const source = res.data.authorContent
        const nextSource = spliceChapterSectionIntoSource(source, headingBlockId, flushedAst.blocks)
        if (!nextSource) {
          toast.error('Section heading no longer found in source — save aborted')
          return
        }
        const saveRes = await apiService.updateChapter(
          bookId,
          filename,
          { authorContent: nextSource },
          { returnFull: true },
        )
        if (!saveRes.success || !saveRes.data) {
          toast.error('Failed to save section')
          return
        }
        sourceCacheRef.current.set(filename, saveRes.data)
        setPreviewDirty(false)
        setPreviewRecentlySaved(true)
        if (recentlySavedTimerRef.current) {
          window.clearTimeout(recentlySavedTimerRef.current)
        }
        recentlySavedTimerRef.current = window.setTimeout(() => {
          setPreviewRecentlySaved(false)
          recentlySavedTimerRef.current = null
        }, 1500)
        onSectionContentSaved?.(filename, saveRes.data)
      } catch (err) {
        logger.error('Section save failed', err)
        toast.error('Failed to save section')
      } finally {
        setPreviewSaving(false)
      }
    },
    [previewKey, bookId, entryByKey, onSectionContentSaved],
  )

  const closePreview = useCallback(() => {
    if (previewDirty && !window.confirm('Discard unsaved changes to this section?')) {
      return
    }
    setPreviewKey(null)
    resetPreviewEditState()
  }, [previewDirty, resetPreviewEditState])

  const dirty = useMemo(() => {
    if (draftOrder.length !== initialOrderRef.current.length) {
      return true
    }
    for (let i = 0; i < draftOrder.length; i++) {
      if (draftOrder[i] !== initialOrderRef.current[i]) {
        return true
      }
    }
    return false
  }, [draftOrder])

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    // 300ms long-press matches the editor sidebar TOC — see ChapterSectionsToc.
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 5 } }),
  )

  const containerOf = useCallback(
    (id: string): typeof PLACED_ID | typeof UNPLACED_ID | null => {
      if (id === PLACED_ID) {
        return PLACED_ID
      }
      if (id === UNPLACED_ID) {
        return UNPLACED_ID
      }
      if (draftOrder.includes(id)) {
        return PLACED_ID
      }
      if (draftUnplaced.includes(id)) {
        return UNPLACED_ID
      }
      return null
    },
    [draftOrder, draftUnplaced],
  )

  const detectCollisions: CollisionDetection = useCallback((args) => {
    return closestCenter(args)
  }, [])

  const handleDragStart = useCallback((ev: DragStartEvent) => {
    setActiveDragId(ev.active.id as string)
  }, [])

  const handleDragOver = useCallback(
    (ev: DragOverEvent) => {
      const { active, over } = ev
      if (!over) {
        return
      }
      const activeId = active.id as string
      const overId = over.id as string
      const activeContainer = containerOf(activeId)
      const overContainer = containerOf(overId)
      if (!activeContainer || !overContainer || activeContainer === overContainer) {
        return
      }
      if (activeContainer === PLACED_ID && overContainer === UNPLACED_ID) {
        setDraftOrder((prev) => prev.filter((k) => k !== activeId))
        setDraftUnplaced((prev) => {
          if (prev.includes(activeId)) {
            return prev
          }
          const targetIdx = overId === UNPLACED_ID ? prev.length : prev.indexOf(overId)
          const insertAt = targetIdx === -1 ? prev.length : targetIdx
          const next = [...prev]
          next.splice(insertAt, 0, activeId)
          return next
        })
      } else if (activeContainer === UNPLACED_ID && overContainer === PLACED_ID) {
        setDraftUnplaced((prev) => prev.filter((k) => k !== activeId))
        setDraftOrder((prev) => {
          if (prev.includes(activeId)) {
            return prev
          }
          const targetIdx = overId === PLACED_ID ? prev.length : prev.indexOf(overId)
          const insertAt = targetIdx === -1 ? prev.length : targetIdx
          const next = [...prev]
          next.splice(insertAt, 0, activeId)
          return next
        })
      }
    },
    [containerOf],
  )

  const handleDragEnd = useCallback(
    (ev: DragEndEvent) => {
      const { active, over } = ev
      setActiveDragId(null)
      if (!over) {
        return
      }
      const activeId = active.id as string
      const overId = over.id as string
      if (activeId === overId) {
        return
      }
      const activeContainer = containerOf(activeId)
      const overContainer = containerOf(overId)
      if (activeContainer !== overContainer || !activeContainer) {
        return
      }
      if (activeContainer === PLACED_ID) {
        setDraftOrder((prev) => {
          const oldIdx = prev.indexOf(activeId)
          const newIdx = prev.indexOf(overId)
          if (oldIdx === -1 || newIdx === -1) {
            return prev
          }
          return arrayMove(prev, oldIdx, newIdx)
        })
      } else if (activeContainer === UNPLACED_ID) {
        setDraftUnplaced((prev) => {
          const oldIdx = prev.indexOf(activeId)
          const newIdx = prev.indexOf(overId)
          if (oldIdx === -1 || newIdx === -1) {
            return prev
          }
          return arrayMove(prev, oldIdx, newIdx)
        })
      }
    },
    [containerOf],
  )

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      await onSave(draftOrder)
      onClose()
    } finally {
      setSaving(false)
    }
  }, [draftOrder, onSave, onClose])

  const handleClose = useCallback(() => {
    if (previewDirty && !window.confirm('Discard unsaved changes to the section?')) {
      return
    }
    if (dirty && !window.confirm('Discard unsaved changes?')) {
      return
    }
    onClose()
  }, [dirty, previewDirty, onClose])

  const previewEnabled = Boolean(bookId)

  const renderRow = (sectionKey: string, opts: { showPublishControl?: boolean } = {}) => {
    const entry = entryByKey.get(sectionKey)
    if (!entry) {
      return null
    }
    const actionKey = `${entry.chapter.filename}:${entry.headingBlockId}`
    return (
      <SortableFlatRow
        key={sectionKey}
        sectionKey={sectionKey}
        entry={entry}
        bookFontFamily={bookFontFamily}
        bookDirection={bookDirection}
        showPublishControl={opts.showPublishControl && Boolean(onChapterSectionPublish)}
        onPublishClick={onChapterSectionPublish}
        publishBusy={chapterSectionPublishBusyKey === actionKey}
        readOnly={readOnly}
        onPreview={previewEnabled ? handlePreview : undefined}
        dependencies={dependencies}
        onEditDependency={onEditDependency}
        onRemoveDependency={onRemoveDependency}
        linkMode={linkMode}
        isPickedFrom={pickedFromKey === sectionKey}
        onLinkClick={handleLinkClick}
      />
    )
  }

  const activeEntry = activeDragId ? entryByKey.get(activeDragId) : null

  /** Inner grid: dnd-kit's rectSortingStrategy works with multi-column CSS grid layouts. */
  const gridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(180px, 1fr))', md: 'repeat(auto-fill, minmax(220px, 1fr))' },
    gap: 0.75,
    alignContent: 'start',
  }

  /** Column-level shell shared by both panes. */
  const columnSx = {
    width: { xs: '100%', md: '50%' },
    flexShrink: 0,
    p: { xs: 1, md: 1.5 },
    overflowY: { xs: 'visible', md: 'auto' } as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 0.75,
  }

  const headerSx = {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'text.secondary',
    px: 0.5,
    pb: 0.5,
    borderBottom: '1px solid',
    borderColor: 'divider',
    mb: 0.25,
    position: 'sticky' as const,
    top: 0,
    bgcolor: 'background.default',
    zIndex: 1,
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullScreen PaperProps={{ sx: { bgcolor: 'background.default' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box component="span">Organize Sections</Box>
          {onCreateDependency && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={linkMode ? 'link' : 'reorder'}
              onChange={(_, next) => {
                if (next) {
                  setLinkMode(next === 'link')
                  setPickedFromKey(null)
                }
              }}
            >
              <ToggleButton value="reorder" sx={{ textTransform: 'none', px: 1.25 }}>
                סידור
              </ToggleButton>
              <ToggleButton value="link" sx={{ textTransform: 'none', px: 1.25 }}>
                <LinkIcon fontSize="small" sx={{ mr: 0.5 }} />
                קישור
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {linkMode && (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              {pickedFromKey
                ? `נבחר: ${entryByKey.get(pickedFromKey)?.displayText ?? pickedFromKey} — עכשיו בחר את המקטע השני`
                : 'בחר מקטע, ואז את המקטע שהוא מתקשר אליו'}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 0,
            direction: bookDirection,
            overflow: { xs: 'auto', md: 'hidden' },
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={detectCollisions}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            {/* Placed sections (the actual flatSectionsOrder) */}
            <Box sx={columnSx}>
              <Typography sx={headerSx}>Placed Sections ({draftOrder.length})</Typography>
              <DroppableContainer id={PLACED_ID}>
                <SortableContext items={draftOrder} strategy={rectSortingStrategy}>
                  <Box sx={gridSx}>{draftOrder.map((k) => renderRow(k, { showPublishControl: true }))}</Box>
                </SortableContext>
              </DroppableContainer>
              {draftOrder.length === 0 && !activeDragId && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.disabled',
                    textAlign: 'center',
                    py: 4,
                    fontStyle: 'italic',
                  }}
                >
                  Drag sections here to place them
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                borderInlineStart: { xs: 'none', md: '1px solid' },
                borderTop: { xs: '1px solid', md: 'none' },
                borderColor: 'divider',
              }}
            />

            {/* Unplaced pool */}
            <Box sx={columnSx}>
              <Typography sx={headerSx}>Unplaced Sections ({draftUnplaced.length})</Typography>
              <DroppableContainer id={UNPLACED_ID}>
                <SortableContext items={draftUnplaced} strategy={rectSortingStrategy}>
                  <Box sx={gridSx}>{draftUnplaced.map((k) => renderRow(k))}</Box>
                </SortableContext>
              </DroppableContainer>
              {draftUnplaced.length === 0 && !activeDragId && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.disabled',
                    textAlign: 'center',
                    py: 4,
                    fontStyle: 'italic',
                  }}
                >
                  All sections are placed
                </Typography>
              )}
            </Box>

            <DragOverlay>
              {activeEntry && activeDragId ? (
                <Box sx={{ opacity: 0.95, pointerEvents: 'none', width: 240 }}>
                  <SortableFlatRow
                    sectionKey={activeDragId}
                    entry={activeEntry}
                    bookFontFamily={bookFontFamily}
                    bookDirection={bookDirection}
                    isDragOverlay
                  />
                </Box>
              ) : null}
            </DragOverlay>
          </DndContext>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Section preview — floats above the organize dialog */}
      <Dialog
        open={previewOpen}
        onClose={closePreview}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'min(72vw, 920px)',
            height: 'min(78vh, 760px)',
            bgcolor: 'var(--bg-surface)',
            direction: bookDirection,
          },
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <SearchPreviewPanel
            previewIdentity={previewKey}
            previewTitle={previewTitle}
            previewAst={previewAst}
            previewLoading={previewLoading}
            previewError={previewError}
            book={book ?? null}
            editable={!previewError && !previewLoading && Boolean(previewAst)}
            onChange={handlePreviewChange}
            onSave={handlePreviewSave}
            saving={previewSaving}
            hasUnsavedChanges={previewDirty}
            recentlySaved={previewRecentlySaved}
          />
        </Box>
      </Dialog>
    </>
  )
}
